/**
 * Presenter results view: headline stats, the score histogram, and the
 * LLM-clustered pros/cons. Themes auto-poll (every 10s while revealed), so the
 * columns stay live on their own — no manual refresh control.
 */

import { Loader2 } from "lucide-react";
import { useState } from "react";

import {
  type FeedbackCluster,
  useResetVotes,
  type VoteSummaryResponse,
  type VoteThemesResponse,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import { VoteScoreChart } from "./vote-score-chart";

interface VoteResultsProps {
  summary: VoteSummaryResponse | undefined;
  themes: VoteThemesResponse | undefined;
  themesLoading: boolean;
}

export function VoteResults({ summary, themes, themesLoading }: VoteResultsProps) {
  const average = summary?.average;
  const total = summary?.total ?? 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3">
        <Stat label="Average score" value={average == null ? "—" : average.toFixed(1)} />
        <Stat label="Total votes" value={String(total)} />
      </div>

      <VoteScoreChart distribution={summary?.distribution ?? []} />

      <div className="space-y-3">
        <h3 className="font-heading text-foreground text-sm font-bold">Written feedback</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ThemeColumn
            title="What worked well"
            tone="good"
            clusters={themes?.pros_clusters ?? []}
            loading={themesLoading}
          />
          <ThemeColumn
            title="What could be better"
            tone="bad"
            clusters={themes?.cons_clusters ?? []}
            loading={themesLoading}
          />
        </div>
      </div>

      <div className="flex justify-end">
        <ResetVotesButton total={total} />
      </div>
    </div>
  );
}

/**
 * Quiet, two-step reset. First click arms the confirm (spelling out how many
 * votes it will drop); second fires. Deliberately muted — the destructive
 * confirm uses maroon, never the J&J-red hero budget.
 */
function ResetVotesButton({ total }: { total: number }) {
  const resetVotes = useResetVotes();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");

  const handleReset = async () => {
    setError("");
    logger.debug("[vote] resetting all votes");
    try {
      await resetVotes.mutateAsync();
      logger.debug("[vote] reset succeeded");
      setConfirming(false);
    } catch (err) {
      logger.warn("[vote] reset failed:", err);
      setError("Could not reset votes. Please try again.");
    }
  };

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="text-muted hover:text-foreground text-xs font-medium transition-colors"
      >
        Reset votes
      </button>
    );
  }

  return (
    <div className="flex items-center gap-3">
      {error && <span className="text-destructive text-xs">{error}</span>}
      <button
        type="button"
        onClick={() => void handleReset()}
        disabled={resetVotes.isPending}
        className="text-destructive hover:text-destructive/80 inline-flex items-center gap-1.5 text-xs font-medium transition-colors disabled:opacity-50"
      >
        {resetVotes.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
        Confirm reset — this clears all {total} votes
      </button>
      <button
        type="button"
        onClick={() => {
          setConfirming(false);
          setError("");
        }}
        className="text-muted hover:text-foreground text-xs font-medium transition-colors"
      >
        Cancel
      </button>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-border bg-card rounded-lg border px-4 py-3">
      <p className="text-muted text-xs font-medium">{label}</p>
      <p className="font-heading text-foreground mt-1 text-4xl font-bold tracking-tight">{value}</p>
    </div>
  );
}

function ThemeColumn({
  title,
  tone,
  clusters,
  loading,
}: {
  title: string;
  tone: "good" | "bad";
  clusters: FeedbackCluster[];
  loading: boolean;
}) {
  return (
    // This repo has no bg-good-bg/bg-bad-bg tokens — a subtle tint of the
    // existing green accent / destructive (maroon) tokens stands in, kept
    // faint so it reads as texture, not a block of accent color.
    <div
      className={cn("rounded-lg p-4", tone === "good" ? "bg-jj-green-01/15" : "bg-destructive/10")}
    >
      <h4 className="text-foreground text-sm font-medium">{title}</h4>
      {loading ? (
        // First fetch in flight — don't claim "no feedback" before we know.
        <p className="text-muted mt-2 flex items-center gap-1.5 text-sm">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          Loading themes…
        </p>
      ) : clusters.length === 0 ? (
        <p className="text-muted mt-2 text-sm">No written feedback yet.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {clusters.map((cluster, index) => (
            <li key={index} className="flex items-center gap-2">
              <span className="text-foreground text-sm font-medium">{cluster.label}</span>
              <span className="bg-card text-muted rounded-full px-2 py-0.5 text-xs font-medium">
                {cluster.count}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
